{
  "starter": {
    "title": "What can I build?",
    "intro": "Describe a report in the chat, or start from one of these. A live preview with real data appears here as each section is designed.",
    "dataSources": "Available data sources",
    "fields": "Fields: {{fields}}"
  },
  "starterAreas": {
    "crushing": "Crushing and stockpiles",
    "equipment": "Equipment"
  },
  "outputs": {
    "kpi": "KPI sheet",
    "table": "Table",
    "bar": "Bar chart",
    "pareto": "Pareto",
    "stacked": "Stacked bars",
    "line": "Line chart",
    "waterfall": "Waterfall"
  },
  "page": {
    "title": "Eclipse Report Builder",
    "editing": "Editing: {{name}}",
    "tokens_one": "{{formatted}} token this session",
    "tokens_other": "{{formatted}} tokens this session",
    "startFresh": "Start Fresh",
    "feedback": "Feedback",
    "addFromReport": "Add From Report",
    "saveChanges": "Save Changes",
    "saveReport": "Save Report",
    "save": "Save",
    "saveAsNew": "Save As New Report",
    "previousSession": "You have a previous design session from {{date}}.",
    "previousNamedSession": "You have a previous design session: \"{{name}}\" from {{date}}.",
    "startFreshLower": "Start fresh",
    "resume": "Resume",
    "sessionLoadFailed": "Failed to load the previous session.",
    "sectionsAdded_one": "Added {{count}} section from \"{{name}}\".",
    "sectionsAdded_other": "Added {{count}} sections from \"{{name}}\".",
    "needsOneSection": "A report needs at least one section.",
    "removeSection": "Remove Section",
    "remove": "Remove",
    "removeNamed": "Remove <strong>{{title}}</strong> from this report?",
    "removeUnnamed": "Remove this section from this report?",
    "removeKeeps": "The saved report does not change until you save.",
    "saveFailed": "Failed to save the report.",
    "changesSaved": "Changes saved.",
    "reportSaved": "Report saved.",
    "name": "Name",
    "description": "Description",
    "feedbackTitle": "Report Builder Feedback",
    "feedbackIntro": "Tell us what you were trying to build and what went wrong. The current report definition is sent along with your feedback.",
    "feedbackSendFailed": "Failed to send feedback.",
    "feedbackSent": "Feedback sent. Thank you.",
    "post": "Post",
    "loadFailed": "Failed to load the report.",
    "cannotOpen": "This report could not be opened in the builder."
  },
  "designer": {
    "title": "Report Designer",
    "intro": "Describe the report you want, one section at a time. For example: \"A daily table of ore and waste tonnes by pit, compared to budget for the last 24 hours, month to date, and year to date.\"",
    "designing": "Designing…",
    "placeholder": "Describe a report or a change…",
    "restoreFailed": "The saved session could not be restored.",
    "requestFailed": "The designer chat request failed; please try again.",
    "noDefinition": "That reply did not include a report definition, so the report is unchanged.",
    "invalid": "The AI produced an invalid definition after {{attempts}} attempts: {{errors}}"
  },
  "canvas": {
    "empty": "Describe the report you want in the chat and a live preview will appear here."
  },
  "section": {
    "moveUp": "Move up",
    "moveDown": "Move down",
    "remove": "Remove from this report",
    "noData": "No data for this period.",
    "failedToLoad": "This section failed to load: {{error}}",
    "unknownType": "Unknown section type: {{type}}",
    "renderFailed": "\"{{title}}\" could not be rendered: {{error}}"
  },
  "addFrom": {
    "title": "Add sections from another report",
    "intro": "Every section of the report you pick is appended to the one you are building. Nothing is saved until you save, and the report you pick is left untouched.",
    "none": "There are no other saved reports to add sections from.",
    "parseFailed": "\"{{name}}\" could not be parsed, so its sections cannot be added.",
    "add": "Add sections"
  },
  "badge": {
    "reporting": "Reporting API",
    "internal": "Internal API",
    "reportingHint": "Reporting API; also available to Power BI / Excel via API key",
    "internalHint": "Internal API; available inside Eclipse only"
  },
  "share": {
    "share": "Share this report",
    "wholeMine": "Whole mine",
    "zones": "Share with zones"
  },
  "waterfall": {
    "hours": "Hours",
    "breakDownBy": "Break down by",
    "overall": "Overall"
  },
  "kpiTable": {
    "unit": "Unit",
    "savedReportOnly": "(saved report only)",
    "noRows": "No rows defined.",
    "addComment": "Add comment",
    "commentSaveFailed": "Could not save the comment."
  },
  "metricCards": {
    "target": "Target {{value}} {{unit}}"
  },
  "summaryTable": {
    "total": "Total",
    "groupTotal": "{{group}} Total"
  },
  "commentary": {
    "pending": "Commentary will be generated when the report runs."
  },
  "run": {
    "fetchFailed": "Failed to fetch {{endpoint}}",
    "commentaryFailed": "Commentary generation failed.",
    "superseded": "The request was superseded by a newer run; refresh to retry.",
    "sectionFailed": "Section failed to load."
  },
  "merge": {
    "repeatedTitles": "Both reports have a section called {{titles}}; rename or remove one so the combined report reads clearly.",
    "periodDropped": "\"{{name}}\" covers {{dropped}}; the combined report runs over {{kept}} instead, because one report can only offer the viewer one set of date pickers.",
    "periods": {
      "today": "a single day",
      "mtd": "month to date",
      "last7": "the last 7 days",
      "last30": "the last 30 days",
      "custom": "a custom date range"
    }
  },
  "starters": {
    "daily-kpi-sheet": {
      "title": "Daily KPI sheet",
      "summary": "Day and MTD against budget: ex-pit, ore, grade, rehandle, stocks and fleet"
    },
    "production-by-pit": {
      "title": "Production by pit",
      "summary": "MTD tonnes, BCM and grades, by pit, ore or waste and material"
    },
    "production-vs-forecast": {
      "title": "Production against forecast",
      "summary": "Day and MTD actual, forecast and variance, with measures as rows"
    },
    "time-usage-model": {
      "title": "Time usage model",
      "summary": "Calendar down to effective hours, with every availability and utilisation rate"
    },
    "fleet-availability-trend": {
      "title": "Availability and utilisation trend",
      "summary": "Month to date, one line per vehicle type"
    },
    "hours-waterfall": {
      "title": "Scheduled to operating hours",
      "summary": "Where the hours went: PM, breakdown, standby and delays"
    },
    "crusher-feed-daily": {
      "title": "Daily crusher feed",
      "summary": "Ore tonnes a day this month, with grade on a second axis"
    },
    "stockpile-positions": {
      "title": "Stockpile positions",
      "summary": "Opening, mined in, rehandled, crushed and closing tonnes, with grades"
    },
    "crusher-feed-vs-target": {
      "title": "Crusher feed against target",
      "summary": "Day and MTD crusher feed against the Crushing targets"
    },
    "gc-drilling-metres": {
      "title": "Grade control drilling metres",
      "summary": "Metres a day this month, stacked by GC, RC and RAB"
    },
    "ore-block-reconciliation": {
      "title": "Ore block reconciliation",
      "summary": "Estimated against mined tonnes per ore block, by pit"
    },
    "maintenance-pareto": {
      "title": "Maintenance downtime Pareto",
      "summary": "Month to date down hours by machine category, largest first"
    },
    "hours-by-job-type": {
      "title": "Hours and availability by job type",
      "summary": "Scheduled, breakdown and PM hours and availability, digging and trucking"
    },
    "stoppage-reasons": {
      "title": "Stoppage reasons",
      "summary": "Down hours by stoppage type and each one's share, over dates you pick"
    },
    "co2-savings": {
      "title": "CO2 savings",
      "summary": "The CO2 that removing operating delays would save, by machine model"
    },
    "drill-rig-performance": {
      "title": "Drill rig performance",
      "summary": "Per rig: engine and drilling hours, metres, penetration rate and redrill"
    },
    "blast-hole-metres": {
      "title": "Blast hole metres and redrill rate",
      "summary": "Metres a day stacked by pit, with the redrill rate as a line"
    },
    "blast-results": {
      "title": "Blast results this month",
      "summary": "Per pattern: tonnes, powder factor, cost per tonne, misfires and P80"
    },
    "drilling-accuracy": {
      "title": "Drilling accuracy",
      "summary": "Holes within the design depth tolerance, per rig and pattern"
    },
    "vibration-compliance": {
      "title": "Vibration and airblast compliance",
      "summary": "Readings within limit, and the highest PPV and airblast, per monitor"
    },
    "explosives-reconciliation": {
      "title": "Explosives reconciliation",
      "summary": "Issued, returned, charged and the variance, per blast and product"
    },
    "explosives-stock": {
      "title": "Explosives stock by magazine",
      "summary": "NEQ held against each magazine's licence, with products and expiry"
    },
    "drill-consumables-cost": {
      "title": "Drill consumables cost per metre",
      "summary": "Cost per metre by rig this month, with bit life alongside"
    }
  },
  "catalogue": {
    "targets": {
      "label": "Targets (Budget / Plan / Forecast / Actuals)",
      "summary": "Budget, plan, forecast and actual targets"
    },
    "production": {
      "label": "Mining production",
      "summary": "Tonnes, BCM and grades mined by day, shift, pit and material"
    },
    "operating-hours": {
      "label": "Plant operating hours",
      "summary": "Hours, tonnes and BCM per excavator and truck"
    },
    "machine-hours": {
      "label": "Machine hours by category",
      "summary": "Equipment hours split into operating, breakdown, PM, standby and delays, with fuel and CO2"
    },
    "crushing": {
      "label": "Crusher feed",
      "summary": "Crusher feed by shift, with sources and grades"
    },
    "rehandle": {
      "label": "Stockpile rehandle",
      "summary": "Stockpile rehandle movements by shift"
    },
    "blast-hole-drilling": {
      "label": "Blast hole drilling",
      "summary": "Blast hole metres and holes by rig, pattern and hole type"
    },
    "pattern-blasts": {
      "label": "Pattern blasts",
      "summary": "Each blast fired, with tonnes, BCM and its design"
    },
    "drill-meters": {
      "label": "Drill rig hours and metres",
      "summary": "Engine hours, drilling hours and metres per rig and shift"
    },
    "drill-holes": {
      "label": "Drilled holes",
      "summary": "Each hole drilled, with its depth and tolerance"
    },
    "blast-firings": {
      "label": "Blast firings",
      "summary": "Each firing, with tonnes, explosives and fragmentation"
    },
    "blast-charging": {
      "label": "Blast charging by product",
      "summary": "Explosives and stemming charged, per blast and product"
    },
    "blast-monitoring": {
      "label": "Blast vibration and airblast monitoring",
      "summary": "Vibration and airblast readings against their limits"
    },
    "explosives-transactions": {
      "label": "Explosives register movements",
      "summary": "Every explosives register movement, by magazine"
    },
    "explosives-reconciliation": {
      "label": "Explosives reconciliation by blast",
      "summary": "Issued, returned, charged and the variance, per blast"
    },
    "explosives-stock": {
      "label": "Explosives stock by magazine",
      "summary": "Explosives held in each magazine against its licence"
    },
    "drill-costs": {
      "label": "Drill consumables cost by rig",
      "summary": "Drill consumables cost per rig and day"
    },
    "drill-components": {
      "label": "Drill component changes",
      "summary": "Bits, rods and other components fitted and removed"
    },
    "consumables-stock": {
      "label": "Drill consumables stock by store",
      "summary": "Drill consumables held in each store"
    },
    "dewatering": {
      "label": "Dewatering volumes",
      "summary": "Water pumped each day, per location"
    },
    "tsf-movement": {
      "label": "TSF movement",
      "summary": "Tonnes moved to the tailings storage facility"
    },
    "broken-stock": {
      "label": "Broken stock",
      "summary": "Blasted tonnes still to be mined, per pattern"
    },
    "stoppages": {
      "label": "Machine stoppage minutes",
      "summary": "Stoppage minutes and availability per machine and shift"
    },
    "availability-summary": {
      "label": "Availability and utilisation summary",
      "summary": "Fleet availability and utilisation over the period"
    },
    "time-usage": {
      "label": "Time usage (availability and utilisation)",
      "summary": "Calendar, down, available, utilised and effective hours per machine, shift and task"
    },
    "stockpile-positions": {
      "label": "Stockpile positions",
      "summary": "Daily stockpile balances, with grades"
    },
    "stockpile-movements": {
      "label": "Stockpile movements",
      "summary": "Movements into and out of stockpiles, by shift"
    },
    "geo-drilling": {
      "label": "Grade control drilling",
      "summary": "Grade control drilling metres a day, by type"
    },
    "gc-drilling": {
      "label": "Grade control drilling by rig",
      "summary": "Grade control drilling per rig, shift and programme"
    },
    "ore-block-reconciliation": {
      "label": "Ore block reconciliation",
      "summary": "Block model against mined tonnes and grades"
    },
    "stoppage-events": {
      "label": "Stoppage events",
      "summary": "Individual stoppages, with type, duration and work order"
    },
    "plants": {
      "label": "Plants reference",
      "summary": "Every equipment unit"
    },
    "pits": {
      "label": "Pits reference",
      "summary": "Every pit"
    },
    "locations": {
      "label": "Locations reference",
      "summary": "Every location: pits, crushers, ROM pads, dumps and stockpiles"
    },
    "pit-stages": {
      "label": "Pit stages reference",
      "summary": "Every pit stage, in mining sequence"
    }
  }
}
